import Banner from '@ben/orion/banner';
import FileUploader from '@ben/orion/file-uploader';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  IModularBenefitDetail,
  IModularBenefitRateTable,
  IRateTableData,
} from '@ben/shared/types/columbia';
import Papa from 'papaparse';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import FallbackConfirmationModal from './fallback-confirmation-modal';
import TableContainer from './table-container';
import ValidationDialog from './validation-dialog';

const RateTable = () => {
  const [columnHeaders, setColumnHeaders] = useState<string[]>([]);
  const [dataType, setDataType] = useState<Record<string, string>>({});
  const [isValidated, setIsValidated] = useState<boolean>();
  const [isMissingRateColumn, setIsMissingRateColumn] = useState(false);
  const [rateTable, setRateTable] = useState<IModularBenefitRateTable>();
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  const { control } = useFormContext<IModularBenefitDetail>();

  const { append, remove, update } = useFieldArray({
    name: 'additionalConfig.rateTables',
    control,
  });

  const rateTables = useWatch({
    control,
    name: 'additionalConfig.rateTables',
    exact: true,
  });

  const currentFallbackTable = useMemo(
    () => rateTables.find((rateTable) => rateTable.isFallback),
    [rateTables]
  );

  // Checking if there already is a fallback table, and the validation has been completed, to show warning
  useEffect(() => {
    if (isValidated && currentFallbackTable && rateTable?.isFallback) {
      setIsFallbackModalOpen(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidated, rateTable]);

  const parseCSV = useCallback(
    (file: File) => {
      Papa.parse<IRateTableData>(file, {
        skipEmptyLines: true,
        complete: (result) => {
          void (async () => {
            if (result.data.length === 0) {
              toast.error('CSV file is empty');
              return;
            }

            setIsValidated(false);
            setRateTable({
              data: [],
              isFallback: false,
              key: '',
              effectiveFrom: undefined,
            });

            const columnHeaders = Object.keys(result.data[0]);

            if (columnHeaders.length === 1 && columnHeaders[0] === 'rate') {
              toast.error(
                'File cannot have only rate column, please add additional columns and try again'
              );
              return;
            }

            if (columnHeaders.length !== new Set(columnHeaders).size) {
              toast.error('File cannot have duplicate column names');
              return;
            }

            if (!columnHeaders.includes('rate')) {
              setIsMissingRateColumn(true);
              return;
            }

            setIsMissingRateColumn(false);

            setColumnHeaders(columnHeaders);

            setRateTable({
              data: result.data,
              key: file.name.split('.')[0],
              isFallback: false,
              effectiveFrom: undefined,
            });

            setValidationDialogOpen(true);
          })().catch(console.error);
        },
        error: () => {
          toast.error('Error parsing CSV file');
        },
        header: true,
      });
    },
    [setValidationDialogOpen]
  );

  useEffect(() => {
    const isFallback = currentFallbackTable && rateTable?.isFallback;
    if (
      !isValidated ||
      !rateTable ||
      rateTable.data.length === 0 ||
      isFallback
    ) {
      return;
    }

    const rateTableData = rateTable?.data.map((row: unknown) => {
      const innerObject: Record<string, string | number> = {};
      for (const [key, value] of Object.entries(
        row as Record<string, unknown>
      )) {
        innerObject[key] =
          dataType[key] === 'number' ? Number(value) : (value as string);
      }
      return innerObject;
    });

    append({
      key: rateTable.key,
      data: rateTableData,
      isFallback: rateTable.isFallback,
      effectiveFrom: rateTable.effectiveFrom,
    });

    // Clearing rateTable after action was taken
    setRateTable({
      data: [],
      isFallback: false,
      key: '',
      effectiveFrom: undefined,
    });
    setIsValidated(false);
  }, [dataType, append, isValidated, rateTable, currentFallbackTable]);

  const handleReplaceCSV = useCallback(
    (index: number) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.csv';
      fileInput.addEventListener('change', (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length <= 1) {
                toast.error('CSV file is empty');
                return;
              }
              setRateTable({
                data: [],
                isFallback: false,
                key: '',
                effectiveFrom: undefined,
              });
              setIsValidated(false);
              const columnHeaders = Object.values(
                result.data[0] as Record<string, string>
              );

              if (columnHeaders.length === 1 && columnHeaders[0] === 'rate') {
                toast.error(
                  'File cannot have only rate column, please add additional columns and try again'
                );
                return;
              }

              if (columnHeaders.length !== new Set(columnHeaders).size) {
                toast.error('File cannot have duplicate column names');
                return;
              }

              if (!columnHeaders.includes('rate')) {
                setIsMissingRateColumn(true);
                return;
              }
              setIsMissingRateColumn(false);
              setColumnHeaders(columnHeaders);

              update(index, {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data: result.data?.slice(1).map((row: any) => {
                  const innerObject: Record<string, string> = {};
                  for (const [index, header] of columnHeaders.entries()) {
                    innerObject[header] = row[index];
                  }
                  return innerObject;
                }),
                key: file.name.split('.')[0],
                isFallback: false,
                effectiveFrom: rateTables?.[index]?.effectiveFrom,
              });

              setValidationDialogOpen(true);
            },
          });
        }
      });
      fileInput.click();

      return () => {
        fileInput.remove();
      };
    },
    [update, setValidationDialogOpen, rateTables]
  );

  const handleConfirmFallback = () => {
    // User accepted to replace fallback table, so removing isFallback from other tables
    for (const [index, table] of rateTables.entries()) {
      if (table.isFallback) {
        update(index, {
          ...table,
        });
      }
    }
    setIsFallbackModalOpen(false);
  };

  const handleCloseFallback = () => {
    setIsFallbackModalOpen(false);
    setValidationDialogOpen(true);
  };

  useEffect(() => {
    if (columnHeaders.length === 0) return;

    setDataType(
      columnHeaders.reduce((accumulator, columnHeader) => {
        return {
          ...accumulator,
          [columnHeader]: columnHeader === 'rate' ? 'number' : 'string',
        };
      }, {})
    );
  }, [columnHeaders, setValidationDialogOpen]);

  const handleDeleteTable = useCallback(
    (index: number) => {
      remove(index);
      toast.success('Rate table deleted, please save to confirm changes');
    },
    [remove]
  );

  return (
    <Container>
      <HeaderContainer>
        <Typography variant={TypographyVariant.h2}>Rate Table</Typography>
      </HeaderContainer>

      {rateTables?.map((rateTable: IModularBenefitRateTable, index: number) => (
        <TableContainer
          key={rateTable.id || `${rateTable.key}-${index}`}
          tableName={rateTable.key}
          rowData={rateTable.data}
          id={rateTable.id}
          handleDeleteTable={() => handleDeleteTable(index)}
          handleReplaceCSV={() => handleReplaceCSV(index)}
          index={index}
          isFallback={rateTable.isFallback}
          effectiveFrom={rateTable.effectiveFrom}
        />
      ))}

      <FileUploader
        acceptedFileExtensions={['csv']}
        onFileUpload={(file) => {
          parseCSV(file[0]);
        }}
        uploading={false}
        maxFilesNumber={1}
      />

      {isMissingRateColumn && (
        <Banner
          variant="critical"
          message="Missing requirement. The file uploaded is missing “rate input’. Please amend and upload the file."
        />
      )}

      {rateTable ? (
        <ValidationDialog
          opened={validationDialogOpen}
          onClose={() => setValidationDialogOpen(false)}
          columnHeaders={columnHeaders}
          fileData={rateTable.data}
          dataType={dataType}
          setDataType={setDataType}
          setIsValidated={setIsValidated}
          isFallback={rateTable.isFallback}
          setIsFallback={(isFallback) =>
            setRateTable({
              ...rateTable,
              isFallback,
            })
          }
          effectiveFrom={rateTable.effectiveFrom}
          setEffectiveFrom={(effectiveFrom) =>
            setRateTable({
              ...rateTable,
              effectiveFrom,
            })
          }
        />
      ) : (
        false
      )}

      {rateTable && currentFallbackTable ? (
        <FallbackConfirmationModal
          isOpen={isFallbackModalOpen}
          onClose={handleCloseFallback}
          onConfirm={handleConfirmFallback}
        />
      ) : (
        false
      )}
    </Container>
  );
};

export default memo(RateTable);

const Container = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    padding: ${theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  `}
`;
